// AiWidgetBlock — the reviewed interpreters for the AI-widget block VOCABULARY
// (tickets/opus.md "Per-workspace AI-CREATED widgets"). One component per block
// type, chosen by the block's own `type`; nothing here is generated, generic,
// or eval'd, and a body can only ever ask for one of these six shapes.
//
// The vocabulary was derived by reading the four real shipped module-widget
// bodies (`workspace/apps/bi/symbols/components/Bi*WidgetBody.js`): every one
// of them is a numeral, a caption, a bounded row list, or a labelled bar list.
// The bar treatment below is the house one — the same capsule track + inset
// hairline + proportional fill BiBarRow already uses (DESIGN_LANGUAGE §3,
// "gauges/tracks are pills") — so an AI widget sits in the same visual family
// as an app-store one rather than inventing a second language.
//
// State per block (childrenAs: 'state' from WidgetRenderer): the validated
// block object — `{ type, …bound fields, widgetId }`. The resolved data lives
// on root under the widget's id; every read goes through the registered
// path/format helpers in functions/aiWidgets.js, which walk own-properties only
// and return undefined on any miss.
export const AiWidgetBlock = {
  flow: 'y',
  gap: 'Y',
  minWidth: '0',

  AiWidgetNumeral: { if: (el, s) => s.type === 'numeral' },
  AiWidgetTextBlock: { if: (el, s) => s.type === 'text-block' },
  AiWidgetList: { if: (el, s) => s.type === 'list' },
  AiWidgetChipRow: { if: (el, s) => s.type === 'chip-row' },
  AiWidgetProgressBars: { if: (el, s) => s.type === 'progress-bars' },
  AiWidgetBarChart: { if: (el, s) => s.type === 'bar-chart' }
}

// One big number + its caption — the device every house widget's Mini tier and
// every bi widget headline already uses (tabular numerals, light weight, tight
// leading). An unresolved path renders an em-dash, exactly like bi's own
// not-ready state, never a blank or a crash.
//
// A board numeral may also name what it counts: `unit`, the model's own word in
// the member's language (functions/assistantBoard.js blockDef: "12 viewings",
// "12 დათვალიერება"). It is drawn the way the house hero draws a number and
// its unit (components/HomeWidgetHero.js), so a board shows ONE look for that
// pair — its records stat cards draw the hero itself: the number and the unit
// word share a row that wraps, on the number's baseline, and the unit wraps
// under the number instead of an ellipsis (the hero's QUALITY LAW: those words
// are never cut). `suffix` stays a sign inside the number's own text ("42%").
// With no unit the row holds the number alone and the numeral looks as before.
export const AiWidgetNumeral = {
  flow: 'y',
  gap: 'X',
  minWidth: '0',

  // HomeWidgetHero's row (flex row that wraps, baseline, column gap `Y`). The
  // hero itself is not extended: its Value carries the hero's numeral (`E` /
  // `F` by cell size, weight 300, tracked −0.03em, no ellipsis), its class and
  // grid area, a Tail and a Skeleton — this number keeps its own look.
  Quantity: {
    display: 'flex',
    flexFlow: 'row wrap',
    alignItems: 'baseline',
    columnGap: 'Y',
    minWidth: '0',

    Value: {
      tag: 'span',
      fontSize: 'D',
      fontWeight: '200',
      lineHeight: '0.95',
      color: 'title',
      fontFamily: 'monospace',
      fontVariantNumeric: 'tabular-nums',
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      text: (el, s) => {
        const fetched = el.call('aiWidgetState', s.root, s.widgetId)
        if (!fetched.settled) return '—'
        const value = el.call('resolveWidgetPath', fetched.data, s.valuePath)
        return el.call('formatWidgetValue', value, s.format, {
          prefix: s.prefix,
          suffix: s.suffix
        })
      }
    },

    // The unit word in the look of HomeWidgetHero's Unit (its tokens: `A`,
    // 400, `inactive`, line height 1.3, `overflowWrap: 'anywhere'`): it wraps
    // and is never cut. Hidden with no unit, and while the value is not read
    // yet: the number shows its em dash alone, as the hero hides its unit
    // while it loads. The data read comes first, so it is always subscribed.
    Unit: {
      tag: 'span',
      fontSize: 'A',
      fontWeight: '400',
      lineHeight: '1.3',
      color: 'inactive',
      overflowWrap: 'anywhere',
      minWidth: '0',
      hide: (el, s) => !el.call('aiWidgetState', s.root, s.widgetId).settled || !s.unit,
      text: (el, s) => s.unit || ''
    }
  },

  Caption: {
    tag: 'span',
    hide: (el, s) => !s.label && !s.caption && !s.captionPath,
    fontSize: 'Z',
    color: 'caption',
    lineHeight: '1.3',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => {
      if (s.captionPath) {
        const fetched = el.call('aiWidgetState', s.root, s.widgetId)
        const bound = el.call('resolveWidgetPath', fetched.data, s.captionPath)
        if (bound != null) return String(bound)
      }
      return s.caption || s.label || ''
    }
  }
}

// A static or bound line of prose — captions, empty-state copy, a bound
// summary string. Its TYPOGRAPHIC style is the def's `textStyle` (body |
// caption; `tone` is the old name, still read — aiWidgetTextStyle). A board
// text block may also carry a SEMANTIC tone in its data (`tonePath` -> good |
// warning | critical): a dot in the design system's semantic ink leads the
// words; the words keep their own ink.
export const AiWidgetTextBlock = {
  tag: 'span',
  dataAiText: true,
  dataTextStyle: (el, s) => el.call('aiWidgetTextStyle', s),
  flow: 'x',
  align: 'center',
  gap: 'Y',
  fontSize: (el, s) => (el.call('aiWidgetTextStyle', s) === 'caption' ? 'Y2' : 'Z2'),
  color: (el, s) => (el.call('aiWidgetTextStyle', s) === 'caption' ? 'caption' : 'title'),
  lineHeight: '1.35',
  minWidth: '0',
  ToneDot: {
    tag: 'span',
    dataAiToneDot: true,
    if: (el, s) => {
      if (!s.tonePath) return false
      const fetched = el.call('aiWidgetState', s.root, s.widgetId)
      return !!el.call('aiWidgetToneInk', el.call('resolveWidgetPath', fetched.data, s.tonePath))
    },
    dataTone: (el, s) => {
      const fetched = el.call('aiWidgetState', s.root, s.widgetId)
      return el.call('aiWidgetToneInk', el.call('resolveWidgetPath', fetched.data, s.tonePath))
    },
    boxSize: 'Y',
    flexShrink: '0',
    round: 'radiusPill',
    background: (el, s) => {
      const fetched = el.call('aiWidgetState', s.root, s.widgetId)
      return el.call('aiWidgetToneInk', el.call('resolveWidgetPath', fetched.data, s.tonePath))
    }
  },
  Words: {
    tag: 'span',
    minWidth: '0',
    text: (el, s) => {
      if (s.textPath) {
        const fetched = el.call('aiWidgetState', s.root, s.widgetId)
        const bound = el.call('resolveWidgetPath', fetched.data, s.textPath)
        if (bound != null) return String(bound)
        return s.text || ''
      }
      return s.text || ''
    }
  }
}

// A bounded list of rows: title + optional meta (bi-stock's low-stock rows).
export const AiWidgetList = {
  flow: 'y',
  gap: 'X',
  minWidth: '0',

  Label: {
    tag: 'span',
    hide: (el, s) => !s.label,
    fontSize: 'Y2',
    color: 'caption',
    text: (el, s) => s.label || ''
  },

  Rows: {
    flow: 'y',
    gap: 'X',
    minWidth: '0',
    children: (el, s) =>
      el.call('widgetListItems', el.call('aiWidgetState', s.root, s.widgetId).data, s),
    childExtends: 'AiWidgetListRow',
    childrenAs: 'state'
  },

  Empty: {
    tag: 'span',
    hide: (el, s) =>
      !!el.call('widgetListItems', el.call('aiWidgetState', s.root, s.widgetId).data, s).length,
    fontSize: 'Z2',
    color: 'caption',
    text: (el, s) => el.call('polyglot', s.emptyText || 'workspace.aiWidget.empty')
  }
}

// The row WRAPS rather than squeezing. A short meta ("12 viewings") stays on
// the title's line, at its end; a long one — an AI board writes a phrase —
// drops to its own line. Without the wrap the nowrap meta kept its whole text
// width and the title shrank to its ellipsis: "1. Catchup & Delinquency
// Status" painted as "1.." (Storm board on dev, 2026-10-04). Each span caps at
// the row and ellipsizes on its own line.
export const AiWidgetListRow = {
  flow: 'x',
  flexWrap: 'wrap',
  rowGap: 'X',
  columnGap: 'Z',
  align: 'center space-between',
  minWidth: '0',

  Name: {
    tag: 'span',
    fontSize: 'Z2',
    fontWeight: '500',
    color: 'title',
    minWidth: '0',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    text: (el, s) => s.label
  },
  Meta: {
    tag: 'span',
    hide: (el, s) => !s.meta,
    fontSize: 'Y2',
    color: 'caption',
    minWidth: '0',
    maxWidth: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontVariantNumeric: 'tabular-nums',
    text: (el, s) => s.meta
  }
}

// A row of compact pills — tag/stage summaries.
export const AiWidgetChipRow = {
  flow: 'y',
  gap: 'X',
  minWidth: '0',

  Label: {
    tag: 'span',
    hide: (el, s) => !s.label,
    fontSize: 'Y2',
    color: 'caption',
    text: (el, s) => s.label || ''
  },

  Chips: {
    flow: 'x',
    gap: 'X',
    flexWrap: 'wrap',
    minWidth: '0',
    children: (el, s) =>
      el.call('widgetListItems', el.call('aiWidgetState', s.root, s.widgetId).data, s),
    childExtends: 'AiWidgetChip',
    childrenAs: 'state'
  },

  Empty: {
    tag: 'span',
    hide: (el, s) =>
      !!el.call('widgetListItems', el.call('aiWidgetState', s.root, s.widgetId).data, s).length,
    fontSize: 'Z2',
    color: 'caption',
    text: (el, s) => el.call('polyglot', s.emptyText || 'workspace.aiWidget.empty')
  }
}

export const AiWidgetChip = {
  tag: 'span',
  padding: 'X Y',
  round: 'radiusPill',
  background: 'overlay',
  color: 'title',
  fontSize: 'Y2',
  fontWeight: '500',
  whiteSpace: 'nowrap',
  text: (el, s) => s.label
}

// Label + proportional bar per item (bi-pipeline's stage breakdown) and
// label + value + bar (bi-stores' bar list). The two differ only in whether the
// numeric value is printed alongside, so they share one row component and one
// `showValue` flag rather than two near-identical copies.
export const AiWidgetProgressBars = {
  flow: 'y',
  gap: 'X',
  minWidth: '0',

  Label: {
    tag: 'span',
    hide: (el, s) => !s.label,
    fontSize: 'Y2',
    color: 'caption',
    text: (el, s) => s.label || ''
  },

  Rows: {
    flow: 'y',
    gap: 'X',
    minWidth: '0',
    children: (el, s) => {
      const items = el.call('widgetListItems', el.call('aiWidgetState', s.root, s.widgetId).data, s)
      const peak = el.call('widgetListPeak', items)
      return items.map((item) => ({
        ...item,
        pct: peak > 0 ? Math.max(2, Math.round((item.value / peak) * 100)) : 0,
        display: el.call('formatWidgetValue', item.rawValue, s.format),
        showValue: s.type === 'bar-chart'
      }))
    },
    childExtends: 'AiWidgetBarRow',
    childrenAs: 'state'
  },

  Empty: {
    tag: 'span',
    hide: (el, s) =>
      !!el.call('widgetListItems', el.call('aiWidgetState', s.root, s.widgetId).data, s).length,
    fontSize: 'Z2',
    color: 'caption',
    text: (el, s) => el.call('polyglot', s.emptyText || 'workspace.aiWidget.empty')
  }
}

// bar-chart IS progress-bars with the value printed — same interpreter, so the
// two block types can never drift apart visually.
export const AiWidgetBarChart = { extends: 'AiWidgetProgressBars' }

// One bar. The house capsule track: `overlay` ground, inset hairline, pill
// radius, proportional fill — copied from BiBarRow so both planes read as one
// system. The fill uses the widget's own resolved hue ink when it has one and
// the adaptive title ink otherwise, never a raw colour.
export const AiWidgetBarRow = {
  display: 'grid',
  gridTemplateColumns: (el, s) =>
    s.showValue ? 'minmax(4em, 8em) 1fr auto' : 'minmax(4em, 8em) 1fr',
  gap: 'Z',
  align: 'center',
  minWidth: '0',

  BarLabel: {
    tag: 'span',
    fontSize: 'Z2',
    fontWeight: '500',
    color: 'title',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    text: (el, s) => s.label
  },

  Track: {
    position: 'relative',
    height: 'Y',
    round: 'radiusPill',
    background: 'overlay',
    boxShadow: 'inset 0 hairline hairline black.12',
    overflow: 'hidden',

    Fill: {
      position: 'absolute',
      top: '0',
      bottom: '0',
      left: '0',
      round: 'radiusPill',
      transitionProperty: 'width',
      transition: 'C defaultBezier',
      width: (el, s) => s.pct + '%',
      background: 'title'
    }
  },

  BarValue: {
    tag: 'span',
    hide: (el, s) => !s.showValue,
    fontSize: 'Z2',
    fontWeight: '700',
    color: 'title',
    whiteSpace: 'nowrap',
    textAlign: 'right',
    fontVariantNumeric: 'tabular-nums',
    text: (el, s) => s.display
  }
}
